import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// Header chip: TTS attempts across all Gemini keys in the last 24 h.
// Click → the count per key; click a key → its attempts, newest first.
// «ok» / «failed» is Google's verdict: ok means Google answered 200 and spent
// the key's quota — even when the take was then cut on silence. Our side of
// it (cut, no audio, …) is a note under the attempt, not a failure.

// Fired by the places that start a take, so the chip refreshes the moment a
// take ends instead of on its next poll.
export const TTS_ATTEMPTS_CHANGED = 'tts-attempts-changed';
export const notifyTtsAttempt = () => {
  try { window.dispatchEvent(new Event(TTS_ATTEMPTS_CHANGED)); } catch (e) { /* no window */ }
};

const OUTCOME_NOTE = {
  truncated: 'cut on silence',
  no_audio: 'Google took it, no audio came back',
  stream_broken: 'stream broke mid-way',
  rate_limited: 'rate-limited',
  key_rejected: 'key refused — its Google project is denied',
  egress_failed: 'no exit reached Google',
  error: 'error',
};

const SOURCE_LABEL = { manual: 'take', batch: 'batch', sync: 'sync' };

const fmtTime = (iso) => {
  const d = new Date(iso);
  const hms = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return hms;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hms}`;
};
const fmtHm = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const TtsAttemptsChip = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const buttonRef = useRef(null);
  const popRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/tts-attempts', { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setLoadError([j.error || `HTTP ${r.status}`, j.details].filter(Boolean).join('\n'));
        return;
      }
      setData(j);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || String(e));
    }
  }, []);

  // Refresh on open, then every 20 s while open and every minute while closed
  // (not in a hidden tab); plus right after a take ends.
  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, open ? 20000 : 60000);
    return () => clearInterval(id);
  }, [load, open]);
  useEffect(() => {
    window.addEventListener(TTS_ATTEMPTS_CHANGED, load);
    return () => window.removeEventListener(TTS_ATTEMPTS_CHANGED, load);
  }, [load]);

  useEffect(() => {
    const onDown = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target) &&
          popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = buttonRef.current && buttonRef.current.getBoundingClientRect();
    if (r) setPos({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
    setOpen(true);
  };

  const failed = data ? data.failed : 0;

  return (
    <div className="tts-chip-control">
      <button
        ref={buttonRef}
        className={`tts-chip ${failed > 0 ? 'has-failed' : ''}`}
        onClick={toggle}
        title={loadError
          ? `TTS attempts could not be loaded: ${loadError}`
          : 'TTS attempts on all Gemini keys in the last 24 h (Google\'s verdict) — click for each key'}
      >
        <span className="tts-chip-label">TTS 24h</span>
        <span className="tts-chip-count">{data ? data.total : (loadError ? '?' : '…')}</span>
        {failed > 0 && <span className="tts-chip-failed">{failed} ✗</span>}
      </button>
      {open && pos && ReactDOM.createPortal(
        <div className="lens-popover tts-popover" ref={popRef} style={{ top: pos.top, right: pos.right }}>
          <div className="lens-popover-head">
            <span>TTS attempts · last 24 h</span>
            <button className="lens-showall" onClick={load}>Refresh</button>
          </div>
          {loadError && (
            <div className="tts-pop-error">
              Could not load the attempt log.
              <pre>{loadError}</pre>
            </div>
          )}
          {data && (
            <>
              <div className="tts-pop-summary">
                <b>{data.total}</b> attempt{data.total === 1 ? '' : 's'}
                {' · '}<span className="tts-ok">{data.ok} ok</span>
                {' · '}<span className={data.failed ? 'tts-failed' : ''}>{data.failed} failed</span>
              </div>
              <div className="tts-pop-hint">
                ok = Google answered 200 and spent the key's quota, whatever became of the audio.
              </div>
              {data.keys.map((k) => {
                const isOpen = openKey === k.keyMasked;
                return (
                  <div key={k.keyMasked} className={`tts-key ${isOpen ? 'open' : ''}`}>
                    <button className="tts-key-row" onClick={() => setOpenKey(isOpen ? null : k.keyMasked)}>
                      <span className="tts-key-caret">{isOpen ? '▾' : '▸'}</span>
                      <span className="tts-key-name">
                        {k.label}
                        {!k.inPool && <span className="tts-key-out" title="Not in the server's key pool now"> · not in pool</span>}
                      </span>
                      <span className="tts-key-counts">
                        <b>{k.total}</b>
                        {k.total > 0 && <span className="tts-ok"> {k.ok} ok</span>}
                        {k.failed > 0 && <span className="tts-failed"> {k.failed} ✗</span>}
                      </span>
                    </button>
                    <div className="tts-key-sub">
                      <span className="tts-key-mask">{k.keyMasked}</span>
                      {' · '}{k.lastAt ? `last ${fmtTime(k.lastAt)}` : 'no attempts'}
                      {k.restingUntil && (
                        <span className="tts-failed"> · resting until {fmtHm(k.restingUntil)} ({k.restReason})</span>
                      )}
                    </div>
                    {isOpen && (
                      <ol className="tts-attempts">
                        {k.attempts.length === 0 && <li className="tts-empty">No attempts in the last 24 h.</li>}
                        {k.attempts.map((a, i) => {
                          const note = OUTCOME_NOTE[a.outcome];
                          return (
                            <li key={`${a.at}-${i}`} className="tts-attempt">
                              <div className="tts-attempt-line">
                                <span className="tts-attempt-time">{fmtTime(a.at)}</span>
                                <span className={`tts-pill ${a.googleOk ? 'ok' : 'failed'}`}>
                                  {a.googleOk ? 'ok' : (a.httpStatus ? String(a.httpStatus) : 'not reached')}
                                </span>
                                <span className="tts-attempt-meta">
                                  {SOURCE_LABEL[a.source] || a.source}
                                  {' · '}{a.chars} ch
                                  {' · '}{Math.round((a.durationMs || 0) / 1000)} s
                                  {a.user ? ` · ${a.user}` : ''}
                                </span>
                              </div>
                              {note && (
                                <div className={`tts-attempt-note ${a.googleOk ? '' : 'tts-failed'}`}>
                                  {note}{a.outcome === 'truncated' && a.voicedSec ? ` at ~${a.voicedSec} s` : ''}
                                </div>
                              )}
                              {a.error && (
                                <details className="tts-attempt-err">
                                  <summary>error text</summary>
                                  <pre>{a.error}</pre>
                                </details>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default TtsAttemptsChip;
